
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { User } from "@supabase/supabase-js";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";

interface UserMenuProps {
  isAuthenticated: boolean;
  logout: () => void;
  isMarketingSite?: boolean;
  user?: User | null;
  simplified?: boolean;
}

const UserMenu = ({ isAuthenticated, logout, isMarketingSite, user, simplified = false }: UserMenuProps) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const getUserFullName = () => {
    const firstName = user?.user_metadata?.name || user?.user_metadata?.first_name || '';
    return firstName.trim();
  };
  
  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoggingOut(true);
    
    try {
      console.log("UserMenu: Logout clicked");
      await logout();
      // Navigation is handled in the logout function
    } catch (error) {
      console.error("UserMenu logout error:", error);
      toast.error("Failed to sign out, please try again");
    } finally {
      setIsLoggingOut(false);
    }
  };
  
  return isAuthenticated ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 text-white text-sm font-medium hover:opacity-80 transition-opacity">
          <span className="px-2 py-1 rounded-md animate-pulse-subtle border border-purple-500/50 bg-purple-500/10 shadow-sm">
            {getUserFullName()}
          </span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="w-full">Dashboard</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings" className="w-full">Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={handleLogout} 
          disabled={isLoggingOut}
          className="cursor-pointer"
        >
          {isLoggingOut ? "Signing out..." : "Sign out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <div className="flex items-center gap-2">
      <Link to="/login">
        <Button variant="ghost" className="text-white hover:text-primary">
          Sign in
        </Button>
      </Link>
      <Link to="/register">
        <Button variant="default" className="bg-purple-600 hover:bg-purple-700 text-white">
          Sign up
        </Button>
      </Link>
    </div>
  );
};

export default UserMenu;
